// LoginUser.js
import React, {useState, useEffect} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MessageModal } from '../shop/modules/MessageModal';
import { motion, AnimatePresence } from 'framer-motion';
import useAuth from '../components/hooks/hooks-e-commerce/useAuth';
import { showToast } from './ToastNotification';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { DatePickerField } from './DatePickerField';
import './loginuser.scss';
import '../css/fonts/fonts.scss';

export const LoginUser = ({
    onClose, 
    initialView = 'login', 
    onSwitchToAccount, 
    onSwitchToLogin, 
    onSwitchToRegisterStep1,
    onSwitchToRegisterStep2 }) => {

     // Stato interno per la view attuale
    const [currentView, setCurrentView] = useState(initialView);
    const [direction, setDirection] = useState('left'); // Per controllare la direzione dell'animazione 
    //const navigate = useNavigate();
    const { register, login,  loading, error: authError, resetError } = useAuth(); // Rinomina error

     // Stati per mostrare/nascondere password
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationEmail, setVerificationEmail] = useState('');

    // Formik per il login
    const loginFormik = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Email non valida')
                .required('Email obbligatoria'),
            password: Yup.string()
                .min(6, 'La password deve essere di almeno 6 caratteri')
                .required('Password obbligatoria'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                // 🔥 NORMALIZZA EMAIL IN LOWERCASE
                const normalizedValues = {
                    ...values,
                    email: values.email.toLowerCase().trim()
                };

                console.log('Login values:', normalizedValues);

                const result = await login(normalizedValues);
                console.log('🔍 RESULT COMPLETO:', result);
                console.log('🔍 USER:', result.user);

                if(result.success) {
                    console.log('✅ Login completato con successo!');

                    // 🔥 CONTROLLA SE È ADMIN (usa status, non status_utente)
                    const isAdmin = result.user?.status === 'admin' || 
                                    result.user?.email === 'admin@gmail.com';
                    
                    console.log('👑 isAdmin:', isAdmin);
                    console.log('👑 status ricevuto:', result.user?.status);

                    // Mostra toast
                    showToast(
                        `🎉 Benvenuto ${result.user?.nome || result.user?.nome_utente || 'utente'}!`,
                        'success',
                        3000
                    );

                    // Resetta i form
                    registerStep2Formik.resetForm();
                    registerStep1Formik.resetForm();
                    loginFormik.resetForm();
                    
                    // Chiudi il modal UNA VOLTA SOLA
                    onClose();

                    // 🔥 REDIRECT UNICO (dopo che il modal si è chiuso)
                    setTimeout(() => {
                        if (isAdmin) {
                            console.log('🚀 Reindirizzo a dashboard admin');
                            window.location.href = '/admin';
                        } else {
                            console.log('👤 Utente normale, resto sulla pagina');
                        }
                    }, 500);

                } else {
                    console.error('❌ Errore nel login:', result.error);
                    showToast(
                        `❌ ${result.error || 'Errore durante il login'}`,
                        'error',
                        4000
                    );
                }
            } catch (err) {
                console.error('🔥 Errore durante il login:', err);
                showToast(
                    `❌ Errore di connessione. Riprova più tardi.`,
                    'error',
                    4000
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Formik per il primo step di registrazione
    const registerStep1Formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            confirmPassword: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Email non valida')
                .required('Email obbligatoria'),
            password: Yup.string()
                .min(6, 'La password deve essere di almeno 6 caratteri')
                .required('Password obbligatoria'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Le password non corrispondono')
                .required('Conferma password obbligatoria'),
        }),
        onSubmit: (values) => {
            console.log('Step 1 values:', values);
            //handleSwitchToRegisterStep2();
        },
    });

    // 2. Crea una funzione separata per la validazione e cambio step
    const validateAndGoToStep2 = () => {
        // Valida tutti i campi
        registerStep1Formik.validateForm().then(errors => {
            if (Object.keys(errors).length === 0) {
                // 🔥 NORMALIZZA EMAIL IN LOWERCASE PRIMA DI PASSARE ALLO STEP 2
                const normalizedEmail = registerStep1Formik.values.email.toLowerCase().trim();
                registerStep1Formik.setFieldValue('email', normalizedEmail);

                // Se non ci sono errori, passa allo step 2
                setDirection('right');
                setCurrentView('regiterStep2');
                if (onSwitchToRegisterStep2) onSwitchToRegisterStep2();
            } else {
                // Se ci sono errori, mostra i campi toccati
                registerStep1Formik.setTouched({
                    email: true,
                    password: true,
                    confirmPassword: true
                }, false);
            }
        });
    };

     // Funzione toggle generica
    const togglePasswordVisibility = (field) => {
        switch(field) {
            case 'login':
                setShowLoginPassword(!showLoginPassword);
                break;
            case 'register':
                setShowRegisterPassword(!showRegisterPassword);
                break;
            case 'confirm':
                setShowConfirmPassword(!showConfirmPassword);
                break;
            default:
                break;
        }
    };

    // Resetta gli stati di visualizzazione password quando cambia view
    useEffect(() => {
        // Resetta tutti gli stati di visualizzazione password
        setShowLoginPassword(false);
        setShowRegisterPassword(false);
        setShowConfirmPassword(false);
    }, [currentView]);

    // Formik per il secondo step di registrazione
    const registerStep2Formik = useFormik({
        initialValues: {
            nome: '',
            cognome: '',
            telefono: '',
            data_nascita: '',
            newsletter: false
        },
        validationSchema: Yup.object({
            nome: Yup.string()
                .min(2, 'Il nome deve essere di almeno 2 caratteri')
                .required('Nome obbligatorio'),
            cognome: Yup.string()
                .min(2, 'Il cognome deve essere di almeno 2 caratteri')
                .required('Cognome obbligatorio'),
            telefono: Yup.string()
                .matches(/^[0-9+\-\s]+$/, 'Numero di telefono non valido'),
            data_nascita: Yup.date()
                .max(new Date(), 'La data di nascita non può essere futura')
                .nullable(), // Permetti valore nullo
            /*codfiscale: Yup.string()
                .length(16, 'Il codice fiscale deve essere di 16 caratteri')
                .matches(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/, 'Formato codice fiscale non valido'), */
            newsletter: Yup.boolean()
        }),

        onSubmit: async (values, { setSubmitting }) => {
            try {
                console.log('📦 Step 2 values:', values);
                
                const step1Values = {
                    ...registerStep1Formik.values,
                    email: registerStep1Formik.values.email.toLowerCase().trim()
                };

                const allValues = {
                    ...step1Values,
                    ...values
                };
                
                delete allValues.confirmPassword;
                
                console.log('📤 Invio dati completi:', { ...allValues, password: '***' });
                
                resetError();
                
                const result = await register(allValues);
                
                if (result.success) {
                    console.log('✅ Registrazione processata:', result);
                    
                    if (result.requiresVerification) {
                        console.log('🔴 Apro modal di verifica per:', result.email);
                        setVerificationEmail(result.email || allValues.email);

                        registerStep2Formik.resetForm();
                        registerStep1Formik.resetForm();
                        loginFormik.resetForm();

                        setTimeout(() => {
                            setShowVerificationModal(true);
                        }, 100);

                    } else {
                        console.log('ℹ️ Modalità legacy - senza verifica email');
                        showToast(
                            `🎉 Registrazione completata! Benvenuto ${values.nome || 'utente'}!`,
                            'success',
                            4000
                        );
                        
                        registerStep2Formik.resetForm();
                        registerStep1Formik.resetForm();
                        loginFormik.resetForm();
                        
                        setTimeout(() => {
                            onClose();
                        }, 1500);
                    }
                } else {
                    console.error('❌ Errore registrazione:', result.error);
                    showToast(
                        `❌ ${result.error || 'Errore durante la registrazione'}`,
                        'error',
                        4000
                    );
                }
            } catch (err) {
                console.error('🔥 Errore durante la registrazione:', err);
                showToast(
                    `❌ Errore di connessione. Riprova più tardi.`,
                    'error',
                    4000
                );
            } finally {
                setSubmitting(false);
            }
        },
    });
        
    // Calcola forza password per step 1
    const getPasswordStrength = (password) => {
        if (!password) return '';
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        
        if (strength <= 2) return 'weak';
        if (strength <= 4) return 'medium';
        return 'strong';
    };

    const passwordStrength = getPasswordStrength(registerStep1Formik.values.password);
    const strengthText = {
        weak: 'Debole',
        medium: 'Media',
        strong: 'Forte'
    }[passwordStrength] || '';

     // Sincronizza con initialView quando cambia
    useEffect(() => {
        setCurrentView(initialView);
    }, [ initialView]);

    // Aggiungi dopo gli altri useEffect
    useEffect(() => {
        // Controlla se l'URL contiene il parametro verified=true
        const urlParams = new URLSearchParams(window.location.search);
        const isVerified = urlParams.get('verified') === 'true';
        const email = urlParams.get('email');
        const alreadyVerified = urlParams.get('already') === 'true';
        
        if (isVerified && email) {
            // Rimuovi il parametro dall'URL senza ricaricare la pagina
            window.history.replaceState({}, document.title, window.location.pathname);
            
            if (alreadyVerified) {
                showToast(
                    `✅ Email già verificata! Puoi accedere con ${email}`,
                    'success',
                    3000
                );
            } else {
                showToast(
                    `🎉 Email verificata con successo! Ora puoi accedere con ${email}`,
                    'success',
                    3000
                );
            }
            
            // 🔥 Apri il modal di login automaticamente
            setTimeout(() => {
                if (onSwitchToLogin) {
                    onSwitchToLogin();
                }
            }, 1500);
        }
    }, [onSwitchToLogin]);
    
    const handleSwitchToAccount = () => {
        setDirection('right');
        loginFormik.resetForm();
        registerStep1Formik.resetForm();
        setShowLoginPassword(false);
        setShowRegisterPassword(false);
        setShowConfirmPassword(false);
        setCurrentView('regiterStep1');
        if (onSwitchToRegisterStep1) onSwitchToRegisterStep1();
    };
    
    const handleSwitchToLogin = () => {
        setDirection('left');
        loginFormik.resetForm();
        registerStep1Formik.resetForm();
        setShowLoginPassword(false);
        setShowRegisterPassword(false);
        setShowConfirmPassword(false);
        setCurrentView('login');
        if (onSwitchToLogin) onSwitchToLogin();
    };

    // Funzione per chiudere il modal di verifica
    const handleVerificationModalClose = () => {
        setShowVerificationModal(false);
        onClose(); // Chiudi anche il modal principale
    };

    // Varianti per le animazioni
    const slideVariants = {
        enter: (direction) => ({
            x: direction === 'right' ? 300 : -300,
            opacity: 0,
            scale: 0.8
        }),
        center: {
            x: 0,
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 200,
                damping: 20,
                mass: 1
            }
        },
        exit: (direction) => ({
            x: direction === 'right' ? -300 : 300,
            opacity: 0,
            scale: 0.9,
            transition: {
                duration: 0.2, // Aumentato da 0.2 a 0.3
                ease: "easeInOut"
            }
        })
    };

    const fadeVariants = {
        enter: {
            opacity: 0,
            y: 20
        },
        center: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            y: -20,
            transition: {
                duration: 0.2
            }
        }
    };

    if (loading) return <div className="header-loading">Caricamento...<span className="loggin-spinner">⏳</span></div>;;

    return (
        <div className="login__modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>
                    <span>X</span>
                </button>

                {/* Mostra errori globali */}
                {authError && (
                    <div className="alert alert-danger" style={{ 
                        marginBottom: '1rem',
                        padding: '10px 15px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        borderRadius: '5px',
                        border: '1px solid #f5c6cb'
                    }}>
                        ❌ {authError}
                    </div>
                )}

                <AnimatePresence mode="wait" custom={direction}>
                    {currentView === 'login' && (
                        <motion.div
                            key="login"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="ctn-form"
                        >
                            {authError && (
                                <div className="alert alert-danger">
                                {typeof error === 'object' ? authError.message : authError}
                                </div>
                            )}
                            <span className='form__title'>ACCEDI AL TUO CONTO PERSONALE</span>
                            <form onSubmit={loginFormik.handleSubmit}>
                                <div className="form-field">
                                    <label htmlFor="email" className="field__label">
                                        Email <span className="required">*</span>
                                    </label>
                                    
                                        <input
                                            type="email"
                                            placeholder='Digita il tuo e-mail'
                                            className={`input__elt ${loginFormik.touched.email && loginFormik.errors.email ? 'error' : ''
                                            }`}  
                                            id='email'
                                            name='email'
                                            onChange={(e)=>{loginFormik.handleChange(e)}}
                                            onBlur={(e)=>{
                                                const normalizedValue = e.target.value.toLowerCase().trim();
                                                loginFormik.setFieldValue('email', normalizedValue);
                                                loginFormik.handleBlur(e);
                                            }}
                                            value={loginFormik.values.email}
                                            disabled={loading}
                                        />
                                        {loginFormik.touched.email && loginFormik.errors.email && (
                                            <span className="error-message">{loginFormik.errors.email}</span>
                                        )}
                                    
                                </div>
                                <div className="form-field">
                                    <label htmlFor="password" className="field__label">
                                        Password <span className="required">*</span>
                                    </label>
                                    <div className="password-input-wrapper">
                                        <input 
                                        type={showLoginPassword ? "text" : "password"}
                                        placeholder='Digita la tua password'
                                        className={`input__elt ${
                                            loginFormik.touched.password && loginFormik.errors.password ? 'error' : ''
                                        }`}   
                                            id='password'
                                            name='password'
                                            onChange={loginFormik.handleChange}
                                            onBlur={loginFormik.handleBlur}
                                            value={loginFormik.values.password}
                                            disabled={loading}
                                        />
                                        <button 
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => togglePasswordVisibility('login')}
                                            aria-label={showLoginPassword ? "Nascondi password" : "Mostra password"}
                                        >
                                            {showLoginPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>    
                                        {loginFormik.touched.password && loginFormik.errors.password && (
                                            <span className="error-message">{loginFormik.errors.password}</span>
                                        )}
                                </div>
                                <div className="form-pwd__forggeted">
                                    <a href="/">Password dimenticata ?</a>
                                </div>
                                <div className="cmd__field">           
                                    <button 
                                        type="submit" 
                                        className="field__btn"
                                        disabled={loading}
                                        >    
                                        {loading ? 'Caricamento...' : 'Collegamento'}    
                                    </button>
                                </div>
                            </form>
                            <div className="link-account">
                                Non sei ancora un membro? <span onClick={handleSwitchToAccount}>
                                Creare un account
                                </span>        
                            </div>
                        </motion.div>
                    )}

                    {currentView === 'regiterStep1' && (
                        <motion.div
                            key="registerStep1"
                            custom={direction}
                            variants={fadeVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="ctn-form"
                        >
                            {authError && (
                                <div className="alert alert-danger">
                                {typeof error === 'object' ? authError.message : authError}
                                </div>
                            )}
                            <span className='form__title'>NON HO UN ACCOUNT, MI ISCRIVO</span>
                            <form onSubmit={registerStep1Formik.handleSubmit}>
                                <div className="form-field">
                                    <label htmlFor="email" className="field__label">
                                        Email <span className="required">*</span>
                                    </label>
                                        <input
                                            type="email"
                                            placeholder='Digita il tuo e-mail'
                                            className={`input__elt ${registerStep1Formik.touched.nome && registerStep1Formik.errors.nome ? 'error' : ''
                                            }`}  
                                            id='email'
                                            name='email'
                                            onChange={(e)=>{registerStep1Formik.handleChange(e)}}
                                            onBlur={(e)=>{
                                                // Normalizza quando esce dal campo
                                                const normalizedValue = e.target.value.toLowerCase().trim();
                                                registerStep1Formik.setFieldValue('email', normalizedValue);
                                                registerStep1Formik.handleBlur(e);
                                            }}
                                            value={registerStep1Formik.values.email}
                                            disabled={loading}
                                        />
                                        {registerStep1Formik.touched.email && registerStep1Formik.errors.email && (
                                            <span className="error-message">{registerStep1Formik.errors.email}</span>
                                        )}
                                    
                                </div>
                                <div className="form-field">
                                    <label htmlFor="password" className="field__label">
                                        Password <span className="required">*</span>
                                    </label>
                                    <div className="password-input-wrapper">
                                        <input 
                                            type={showRegisterPassword ? "text" : "password"}
                                            placeholder='Digita la tua password'
                                            className={`input__elt ${registerStep1Formik.touched.password && registerStep1Formik.errors.password ? 'error' : ''}`}
                                            id='password'
                                            name='password'
                                            onChange={registerStep1Formik.handleChange}
                                            onBlur={registerStep1Formik.handleBlur}
                                            value={registerStep1Formik.values.password}
                                            disabled={loading}
                                        />
                                        <button 
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => togglePasswordVisibility('register')}
                                            aria-label={showRegisterPassword ? "Nascondi password" : "Mostra password"}
                                        >
                                            {showRegisterPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>        
                                    {registerStep1Formik.touched.password && registerStep1Formik.errors.password && (
                                        <span className="error-message">{registerStep1Formik.errors.password}</span>
                                    )}
                                    {registerStep1Formik.values.password && (
                                        <div className="password-strength">
                                            <div className="strength-meter">
                                            <div className={`strength-bar ${passwordStrength}`}></div>
                                            </div>
                                            <div className="strength-text">
                                            Forza password: {strengthText}
                                            </div>
                                        </div>
                                    )}
                                    <span className="form-text">Minimo 6 caratteri</span>
                                </div>
                                <div className="form-field">
                                    <label htmlFor="confirmPassword" className="field__label">
                                        Password (conferma) <span className="required">*</span>
                                    </label>
                                    <div className="password-input-wrapper">
                                        <input 
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder='Riscrivi la tua password'
                                            className={`input__elt ${registerStep1Formik.touched.confirmPassword && registerStep1Formik.errors.confirmPassword ? 'error' : ''}`}   
                                            id='confirmPassword'
                                            name='confirmPassword'
                                            onChange={registerStep1Formik.handleChange}
                                            onBlur={registerStep1Formik.handleBlur}
                                            value={registerStep1Formik.values.confirmPassword}
                                            disabled={loading}
                                        />
                                        <button 
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => togglePasswordVisibility('confirm')}
                                            aria-label={showConfirmPassword ? "Nascondi password" : "Mostra password"}
                                        >
                                            {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                    {registerStep1Formik.touched.confirmPassword && registerStep1Formik.errors.confirmPassword && (
                                        <span className="error-message">{registerStep1Formik.errors.confirmPassword}</span>
                                    )}
                                </div>
                                <div className="cmd__field">           
                                    <button 
                                        type="submit" 
                                        className="field__btn"
                                        disabled={loading}
                                        onClick={validateAndGoToStep2}
                                        >
                                        {loading ? 'Caricamento...' : 'Continua la registrazione'}
                                    </button>
                                </div>
                            </form>
                            <div className="link-account">
                                Ho già un account, <span onClick={handleSwitchToLogin}> Accedi</span>
                            </div>
                        </motion.div>
                    )}

                    {currentView === 'regiterStep2' && (
                        <motion.div
                            key="registerStep2"
                            custom={direction}
                            variants={fadeVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="ctn-form"
                        >
                            {authError && (
                                <div className="alert alert-danger">
                                    {typeof error === 'object' ? authError.message : authError}
                                </div>
                            )}

                            <span className='form__title'>FINALIZZO LA REGISTRAZIONE</span>
                            <form onSubmit={registerStep2Formik.handleSubmit}>
                                <div className="form-field">
                                    <label htmlFor="nome" className="field__label">
                                        Nome<span className="required">*</span>
                                    </label>
                                    <div className="field__input">
                                        <input
                                            className={`input__elt ${registerStep2Formik.touched.nome && registerStep2Formik.errors.nome ? 'error' : ''}`} 
                                            type="text"
                                            placeholder='Digita tuo Nome'
                                            id='nome'
                                            name='nome'
                                            onChange={registerStep2Formik.handleChange}
                                            onBlur={registerStep2Formik.handleBlur}
                                            value={registerStep2Formik.values.nome}
                                            disabled={loading}
                                        />
                                        {registerStep2Formik.touched.nome && registerStep2Formik.errors.nome && (
                                            <span className="error-message">{registerStep2Formik.errors.nome}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label htmlFor="cognome" className="field__label">
                                        Cognome<span className="required">*</span>
                                    </label>
                                    <div className="field__input">
                                        <input
                                            className={`input__elt ${ registerStep2Formik.touched.cognome && registerStep2Formik.errors.cognome ? 'error' : ''}`} 
                                            type="text"
                                            placeholder='Digita tuo Cognome'
                                            id='cognome'
                                            name='cognome'
                                            onChange={registerStep2Formik.handleChange}
                                            onBlur={registerStep2Formik.handleBlur}
                                            value={registerStep2Formik.values.cognome}
                                            disabled={loading}
                                        />
                                        {registerStep2Formik.touched.cognome && registerStep2Formik.errors.cognome && (
                                            <span className="error-message">{registerStep2Formik.errors.cognome}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label htmlFor="telefono" className="field__label">
                                        Telefono
                                    </label>
                                    <div className="field__input">
                                        <input
                                            className={`input__elt ${ registerStep2Formik.touched.telefono && registerStep2Formik.errors.telefono ? 'error' : ''}`} 
                                            type="tel"
                                            placeholder='Digita tuo telefono'
                                            id='telefono'
                                            name='telefono'
                                            onChange={registerStep2Formik.handleChange}
                                            onBlur={registerStep2Formik.handleBlur}
                                            value={registerStep2Formik.values.telefono}
                                            disabled={loading}
                                        />
                                        {registerStep2Formik.touched.telefono && registerStep2Formik.errors.telefono && (
                                            <span className="error-message">{registerStep2Formik.errors.telefono}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-field">
                                    <DatePickerField
                                        value={registerStep2Formik.values.data_nascita}
                                        onChange={registerStep2Formik.handleChange}
                                        onBlur={registerStep2Formik.handleBlur}
                                        error={registerStep2Formik.errors.data_nascita}
                                        touched={registerStep2Formik.touched.data_nascita}
                                        disabled={loading}
                                        label="Data di nascita"
                                        placeholder="Seleziona la tua data di nascita"
                                        name="data_nascita"
                                        maxDate={new Date()}
                                    />
                                </div>

                                {/* Newsletter - usando checkbox invece di input text */}
                                <div className="field__checkmark mt-2">
                                    <label className="checkmark__label">
                                        <input
                                            type="checkbox"
                                            id="newsletter"
                                            name="newsletter"
                                            checked={registerStep2Formik.values.newsletter}
                                            onChange={registerStep2Formik.handleChange}
                                            disabled={loading}
                                        />
                                        <span className="checkmark"></span>
                                        Desidero iscrivermi alla newsletter per rimanere aggiornato/a
                                    </label>
                                </div>

                                <div className="cmd__field">           
                                    <button 
                                        type="submit" 
                                        className="field__btn"
                                        disabled={loading || registerStep2Formik.isSubmitting}
                                    >
                                        {loading || registerStep2Formik.isSubmitting ? 'Caricamento...' : 'Creare un account'}
                                    </button>
                                </div>
                            </form>
                            <div className="link-account">
                                Ho già un account, <span onClick={handleSwitchToLogin}> Accedi</span>
                            </div>
                        </motion.div>    
                    )}
                </AnimatePresence>
                {/* MODALE MESSAGGIO VERIFICA EMAIL */}
                <MessageModal
                    isOpen={showVerificationModal}
                    onClose={handleVerificationModalClose}
                    title="Verifica la tua email"
                    message={`Abbiamo inviato un link di verifica a ${verificationEmail}.\n\nControlla la tua posta e clicca sul link per attivare il tuo account.`}
                    icon="📧"
                    buttonText="Ho capito"
                />
            </div> 
        </div>
    );
};
